'use client';

import { ArrowLeft, ArrowRight, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { safeInternalRedirect } from '@invexa/shared';
import { AuthTabs, FormError, IconInput, PasswordInput } from '@/components/auth/fields';
import { Button } from '@/components/ui/button';
import { ApiError, errorMessage, post } from '@/lib/api';

type LoginResult = { user: unknown } | { twoFactor: true; ticket: string };

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Password accepted, account with two-step login: waiting for the code. */
  const [ticket, setTicket] = useState<string | null>(null);

  function done() {
    router.replace(safeInternalRedirect(params.get('next')));
    router.refresh();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await post<LoginResult>('/auth/login', { email, password });
      if ('twoFactor' in r) {
        setTicket(r.ticket);
        setLoading(false);
        return;
      }
      done();
    } catch (err) {
      setError(errorMessage(err));
      setLoading(false);
    }
  }

  if (ticket) {
    return (
      <CodeStep
        ticket={ticket}
        onDone={done}
        onRestart={(message) => {
          setTicket(null);
          setPassword('');
          setError(message);
        }}
      />
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <AuthTabs active="login" />
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Entre na sua conta</h2>
        <p className="mt-1 text-sm text-fg-2">Acompanhe o mercado ao vivo, opere e gerencie seus robôs em um só lugar.</p>
      </div>
      <IconInput icon={Mail} label="E-mail" name="email" type="email" autoComplete="email" inputMode="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@email.com" />
      <div>
        <PasswordInput icon={Lock} label="Senha" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" />
        <div className="mt-2 text-right">
          <Link href={email ? `/esqueci-senha?email=${encodeURIComponent(email)}` : '/esqueci-senha'} className="text-xs font-medium text-fg-2 hover:text-lime">
            Esqueci minha senha
          </Link>
        </div>
      </div>
      {error && <FormError>{error}</FormError>}
      <Button type="submit" variant="primary" size="lg" className="h-12 w-full rounded-xl text-[15px]" loading={loading}>
        Entrar <ArrowRight />
      </Button>
      <p className="pt-2 text-center text-sm text-fg-3">
        Ainda não tem conta?{' '}
        <Link href="/cadastro" className="font-medium text-lime hover:underline">
          Criar conta grátis
        </Link>
      </p>
    </form>
  );
}

/** Second step for accounts with two-step login: the 6-digit code from the app, or a recovery code. */
function CodeStep({ ticket, onDone, onRestart }: { ticket: string; onDone: () => void; onRestart: (message: string | null) => void }) {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await post('/auth/login/2fa', { ticket, code });
      onDone();
    } catch (err) {
      // Time is up or too many tries: back to the password.
      if (err instanceof ApiError && err.code === '2FA_TICKET_EXPIRED') return onRestart(errorMessage(err));
      setError(errorMessage(err));
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="mb-6">
        <span className="mb-4 flex size-11 items-center justify-center rounded-xl bg-lime/10 text-lime">
          <ShieldCheck className="size-5" />
        </span>
        <h2 className="text-2xl font-semibold tracking-tight">Verificação em duas etapas</h2>
        <p className="mt-1 text-sm text-fg-2">
          {recovery ? 'Digite um dos códigos de recuperação que você guardou. Cada um funciona uma vez.' : 'Digite o código de 6 dígitos que aparece no seu app autenticador.'}
        </p>
      </div>
      {recovery ? (
        <IconInput icon={KeyRound} label="Código de recuperação" name="code" autoComplete="off" autoCapitalize="characters" required autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCDE-FGHIJ" className="num tracking-wider" />
      ) : (
        <IconInput
          icon={KeyRound}
          label="Código"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="num text-lg tracking-[0.3em]"
        />
      )}
      {error && <FormError>{error}</FormError>}
      <Button type="submit" variant="primary" size="lg" className="h-12 w-full rounded-xl text-[15px]" loading={loading} disabled={recovery ? code.trim().length < 10 : code.length !== 6}>
        Confirmar <ArrowRight />
      </Button>
      <div className="flex items-center justify-between pt-1 text-xs">
        <button type="button" onClick={() => onRestart(null)} className="flex items-center gap-1 font-medium text-fg-3 hover:text-fg-2">
          <ArrowLeft className="size-3.5" /> Voltar
        </button>
        <button
          type="button"
          onClick={() => {
            setRecovery((r) => !r);
            setCode('');
            setError(null);
          }}
          className="font-medium text-fg-2 hover:text-lime"
        >
          {recovery ? 'Usar o app autenticador' : 'Perdi o celular'}
        </button>
      </div>
    </form>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
